// Enum-like const objects to support dot-access syntax (e.g. TaskPriority.MEDIUM)
export const TaskPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type TaskPriority = (typeof TaskPriority)[keyof typeof TaskPriority];

export const TaskStatus = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  ARCHIVED: 'archived',
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export const TaskCategory = {
  MAINTENANCE: 'maintenance',
  FEEDING: 'feeding',
  WATER_TESTING: 'water_testing',
  DOSING: 'dosing',
  EQUIPMENT: 'equipment',
  LIVESTOCK: 'livestock',
  CLEANING: 'cleaning',
  OBSERVATION: 'observation',
  CUSTOM: 'custom',
} as const;

export type TaskCategory = (typeof TaskCategory)[keyof typeof TaskCategory];

export type RecurrenceFrequency =
  | 'daily'
  | 'every_x_days'
  | 'weekly'
  | 'specific_weekdays'
  | 'monthly'
  | 'custom';

export interface RecurrenceRule {
  // Primary fields used by the API
  frequency: RecurrenceFrequency;
  interval?: number;
  /** 0 = Sunday … 6 = Saturday */
  weekdays?: number[];
  endDate?: string | null;
  /** The API returns the end date in snake_case. */
  end_date?: string | null;
  // Alias fields used by TaskForm.tsx
  type?: RecurrenceFrequency;
  intervalDays?: number;
}

export interface TaskActivityEntry {
  id: string;
  action: string;
  performedBy: string;
  performedAt: string;
  notes?: string | null;
}

/**
 * Which occurrences of a recurring task an edit or delete applies to:
 * only this one, this and all following ones, or the whole series.
 */
export type RecurrenceScope = 'occurrence' | 'following' | 'series';

/**
 * A task as the API returns it. For a recurring task each item is ONE occurrence:
 * `id` is the occurrence id (`<seriesId>_<YYYY-MM-DD>`), `seriesId` the series,
 * `occurrenceDate` the date of the occurrence and `dueDate` its (possibly moved) date.
 */
export interface Task {
  id: string;
  seriesId?: string;
  occurrenceDate?: string | null;
  isRecurring?: boolean;
  /** Occurrence edited on its own (no longer follows series edits). */
  isDetached?: boolean;
  /** UTC instant of dueDate + dueTime (Europe/Berlin wall clock); null without a time. */
  startsAt?: string | null;
  skipReason?: string | null;
  aquariumId: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  category: TaskCategory;
  dueDate: string | null;
  dueTime: string | null;
  recurrenceRule: RecurrenceRule | null;
  completedAt: string | null;
  completedBy: string | null;
  completionNotes: string | null;
  // Alias for completionNotes used by TaskDetail.tsx
  completionNote?: string | null;
  skippedAt?: string | null;
  isAiGenerated: boolean;
  aiSuggestionId: string | null;
  // AI reasoning fields used by TaskDetail.tsx
  aiReasoning?: string | null;
  aiEvidenceLinks?: Array<{ label: string; url: string }> | null;
  // Activity log used by TaskDetail.tsx
  activity?: TaskActivityEntry[] | null;
  createdAt: string;
  updatedAt: string;
  /** E-mail reminder on the due date; for a recurring task it applies to the whole series. */
  emailReminder?: boolean;
  /**
   * 'manual', 'ai_insight', 'dosing_schedule' (generated from a manual dosing plan),
   * 'journal' (detected in a typed journal entry) or 'voice' (from a voice note).
   */
  source?: string;
  /** The journal entry a 'journal' / 'voice' task was created from (docs/voiceFeature.md UC-B). */
  sourceJournalEntryId?: string | null;
  /**
   * The manual dosing plan this task series is generated from; null/absent for other
   * tasks. Such a task is changed, paused or deleted on the dosing page; completing it
   * also logs the dose.
   */
  dosingScheduleId?: string | null;
  /** Dose of a dosing task (or of an accepted AI suggestion). */
  doseAmount?: number | null;
  doseUnit?: string | null;
}

/** Whether a task is generated from a manual dosing plan. */
export function isDosingPlanTask(task: Pick<Task, 'dosingScheduleId'>): boolean {
  return Boolean(task.dosingScheduleId);
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  priority: TaskPriority;
  category: TaskCategory;
  dueDate?: string | null;
  dueTime?: string | null;
  recurrenceRule?: RecurrenceRule | null;
  emailReminder?: boolean;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  priority?: TaskPriority;
  status?: TaskStatus;
  category?: TaskCategory;
  dueDate?: string | null;
  dueTime?: string | null;
  recurrenceRule?: RecurrenceRule | null;
  completionNotes?: string | null;
  /** Series-level setting (also when only one occurrence is edited). */
  emailReminder?: boolean;
}

export interface CompleteTaskInput {
  completionNotes?: string | null;
}

export interface SkipTaskInput {
  reason?: string | null;
}

export interface GroupedTasks {
  overdue: Task[];
  today: Task[];
  upcoming: Task[];
  completed: Task[];
}
